export type SessionTransportState = "playing" | "paused" | "stopped";

export interface SessionTransportSnapshot {
  readonly positionSec: number;
  readonly playbackRate: number;
  readonly pitchSemitones: number;
  readonly state: SessionTransportState;
  readonly updatedAtEpochMs: number;
  readonly seq?: number;
  readonly sampledAtEpochMs?: number;
  readonly issuerId?: string;
}

export interface SessionSongDescriptor {
  readonly youtubeId: string;
  readonly title: string;
  readonly smamsUrl?: string;
  readonly stemUrlsByRole?: Readonly<Record<string, string>>;
}

export interface SessionJoinBootstrap {
  readonly role: "host" | "follower";
  readonly currentSongYoutubeId?: string;
  readonly currentSongTitle?: string;
  readonly currentSongDescriptor?: SessionSongDescriptor;
  readonly transportSnapshot?: SessionTransportSnapshot;
}

function parseFiniteNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function parseTransportState(value: unknown): SessionTransportState | null {
  if (value === "playing" || value === "paused" || value === "stopped") {
    return value;
  }
  return null;
}

function parseTransportSnapshot(value: unknown): SessionTransportSnapshot | undefined {
  if (value === null || value === undefined || typeof value !== "object") {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const positionSec = parseFiniteNumber(raw.positionSec);
  const playbackRate = parseFiniteNumber(raw.playbackRate);
  const pitchSemitones = parseFiniteNumber(raw.pitchSemitones);
  const state = parseTransportState(raw.state);
  const updatedAtEpochMs = parseFiniteNumber(raw.updatedAtEpochMs);
  const sampledAtEpochMs = parseFiniteNumber(raw.sampledAtEpochMs);
  const seqRaw = parseFiniteNumber(raw.seq);
  const seq =
    seqRaw !== null && Number.isInteger(seqRaw) && seqRaw >= 0 ? Math.trunc(seqRaw) : null;
  const issuerId =
    typeof raw.issuerId === "string" && raw.issuerId.trim().length > 0 ? raw.issuerId.trim() : null;
  if (
    positionSec === null ||
    playbackRate === null ||
    pitchSemitones === null ||
    state === null ||
    updatedAtEpochMs === null
  ) {
    return undefined;
  }
  return {
    positionSec,
    playbackRate,
    pitchSemitones,
    state,
    updatedAtEpochMs,
    ...(seq !== null ? { seq } : {}),
    ...(sampledAtEpochMs !== null ? { sampledAtEpochMs } : {}),
    ...(issuerId !== null ? { issuerId } : {}),
  };
}

function parseStemUrlsByRole(value: unknown): Readonly<Record<string, string>> | undefined {
  if (value === null || value === undefined || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const parsed: Record<string, string> = {};
  for (const [role, maybeUrl] of Object.entries(value as Record<string, unknown>)) {
    if (typeof maybeUrl !== "string") {
      continue;
    }
    const trimmedRole = role.trim();
    const trimmedUrl = maybeUrl.trim();
    if (!trimmedRole || !trimmedUrl) {
      continue;
    }
    parsed[trimmedRole] = trimmedUrl;
  }
  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

function parseSongDescriptor(value: unknown): SessionSongDescriptor | undefined {
  if (value === null || value === undefined || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const youtubeId =
    typeof raw.youtubeId === "string" && raw.youtubeId.trim().length > 0
      ? raw.youtubeId.trim()
      : undefined;
  const title =
    typeof raw.title === "string" && raw.title.trim().length > 0 ? raw.title.trim() : undefined;
  if (!youtubeId || !title) {
    return undefined;
  }
  const smamsUrl =
    typeof raw.smamsUrl === "string" && raw.smamsUrl.trim().length > 0
      ? raw.smamsUrl.trim()
      : undefined;
  const stemUrlsByRole = parseStemUrlsByRole(raw.stemUrlsByRole);
  return {
    youtubeId,
    title,
    ...(smamsUrl ? { smamsUrl } : {}),
    ...(stemUrlsByRole ? { stemUrlsByRole } : {}),
  };
}

export function parseSessionJoinBootstrap(
  body: unknown,
  fallbackRole: "host" | "follower",
): SessionJoinBootstrap {
  const raw =
    body !== null && body !== undefined && typeof body === "object"
      ? (body as Record<string, unknown>)
      : {};
  const responseRole = raw.role === "host" || raw.role === "follower" ? raw.role : fallbackRole;
  const currentSongYoutubeId =
    typeof raw.currentSongYoutubeId === "string" && raw.currentSongYoutubeId.trim().length > 0
      ? raw.currentSongYoutubeId.trim()
      : undefined;
  const currentSongTitle =
    typeof raw.currentSongTitle === "string" && raw.currentSongTitle.trim().length > 0
      ? raw.currentSongTitle.trim()
      : undefined;
  const currentSongDescriptor = parseSongDescriptor(raw.currentSongDescriptor);
  const transportSnapshot = parseTransportSnapshot(raw.transportSnapshot);
  return {
    role: responseRole,
    ...(currentSongYoutubeId ? { currentSongYoutubeId } : {}),
    ...(currentSongTitle ? { currentSongTitle } : {}),
    ...(currentSongDescriptor ? { currentSongDescriptor } : {}),
    ...(transportSnapshot ? { transportSnapshot } : {}),
  };
}
